import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSResendEmailSubmitInput,
  type EnrollmentHYCSResendEmailSubmitOutput,
  type EnrollmentHYCSVerifyEmailData,
  type EnrollmentHYCSVerifyEmailSubmitInput,
  type EnrollmentHYCSVerifyEmailSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSVerifyEmailData>
 * @example const response = await getEnrollmentHYCSVerifyEmailData()
 */
export const getEnrollmentHYCSVerifyEmailData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSVerifyEmailData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSVerifyEmailData',
      query: `mutation getEnrollmentHYCSVerifyEmailData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSVerifyEmailData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSVerifyEmailData = createQueryHook<undefined, EnrollmentHYCSVerifyEmailData>(
  getEnrollmentHYCSVerifyEmailData,
)

/**
 * Submit Enrollment HYCS VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSVerifyEmailSubmitOutput>
 * @example const response = await submitEnrollmentHYCSVerifyEmailData();
 */
export const submitEnrollmentHYCSVerifyEmailData = async (
  enrollmentVerifyEmailSubmitInput: EnrollmentHYCSVerifyEmailSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSVerifyEmailSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSVerifyEmailData',
      query: `mutation submitEnrollmentHYCSVerifyEmailData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyEmailSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSVerifyEmailSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSVerifyEmailData = createMutationHook<
  EnrollmentHYCSVerifyEmailSubmitInput,
  EnrollmentHYCSVerifyEmailSubmitOutput
>(submitEnrollmentHYCSVerifyEmailData)

/**
 * Resend  email - VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSResendEmailSubmitOutput>
 * @example const response = await submitEnrollmentHYCSVerifyEmailData();
 */
export const submitEnrollmentHYCSResendEmailData = async (
  enrollmentResendEmailSubmitInput: EnrollmentHYCSResendEmailSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSResendEmailSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSResendEmailData',
      query: `mutation submitEnrollmentHYCSResendEmailData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              email
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentResendEmailSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSResendEmailSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSResendEmailData = createMutationHook<
  EnrollmentHYCSResendEmailSubmitInput,
  EnrollmentHYCSResendEmailSubmitOutput
>(submitEnrollmentHYCSResendEmailData)
